import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { and, eq } from 'tio-ui/utilities';
import { Heading, Text } from 'tio-ui/components/typography';
import Drawer from 'tio-reimagined/components/drawer';
import Button from 'tio-reimagined/components/button';
import { PhBank, PhCheckCircle, PhRadioButton, PhArrowsLeftRight } from 'ember-phosphor-icons';
import type AccountModel from 'tio-common/models/account';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';
import type StoreService from 'ember-data/store';

/**
 * AccountSelectorDrawer — Reimagined version of ChangeActiveAccountModal
 *
 * Uses the reimagined Drawer to let users change which loan account
 * receives employer contributions. Shows a list of linked accounts
 * with current selection highlighted.
 *
 * @example
 * ```gts
 * <AccountSelectorDrawer
 *   @isOpen={{this.showAccountDrawer}}
 *   @onClose={{this.closeAccountDrawer}}
 *   @accounts={{@model.activeAccounts}}
 *   @currentAccount={{@model.accountReceivingContributions}}
 *   @personId={{@model.id}}
 *   @onAccountChanged={{this.reloadData}}
 * />
 * ```
 */
interface AccountSelectorDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** All linked accounts the user can choose from */
    accounts: AccountModel[];
    /** The account currently receiving contributions */
    currentAccount: AccountModel | null;
    /** Person ID for the API call */
    personId: string | null;
    /** Called after the account is successfully changed */
    onAccountChanged?: () => void;
  };
  Element: HTMLDivElement;
}

export default class AccountSelectorDrawer extends Component<AccountSelectorDrawerSignature> {
  @service declare store: StoreService;
  @service('reimagined-toast') declare toast: ReimaginedToastService;

  @tracked private _selectedAccountId: string | null = null;
  @tracked private hasUserSelectedAccount = false;

  get selectedAccountId() {
    if (this.hasUserSelectedAccount) {
      return this._selectedAccountId;
    }

    return this.args.currentAccount?.id ?? null;
  }

  set selectedAccountId(value: string | null) {
    this.hasUserSelectedAccount = true;
    this._selectedAccountId = value;
  }

  get isNewAccountSelected() {
    return this.selectedAccountId !== (this.args.currentAccount?.id ?? null);
  }

  get isSaveDisabled() {
    return !this.selectedAccountId || !this.isNewAccountSelected;
  }

  selectAccount = (accountId: string) => {
    this.selectedAccountId = accountId;
  };

  resetSelection = () => {
    this.hasUserSelectedAccount = false;
    this._selectedAccountId = null;
  };

  handleClose = () => {
    this.resetSelection();
    this.args.onClose();
  };

  updateAccountTask = dropTask(async () => {
    if (!this.selectedAccountId || !this.args.personId) return;

    try {
      const orderedAccountIds = [
        this.selectedAccountId,
        ...this.args.accounts
          .filter((account) => account.id !== this.selectedAccountId)
          .map((account) => account.id),
      ];

      await this.store
        .adapterFor('person')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .updateContributionAccount(this.args.personId, orderedAccountIds);

      this.toast.success('Contribution account updated successfully.');
      this.args.onAccountChanged?.();
      this.handleClose();
    } catch (error) {
      console.error('Error updating contribution account:', error);
      this.toast.error('Failed to update contribution account. Please try again.');
    }
  });

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @size="lg"
      @placement="right"
      ...attributes
    >
      <:header>
        <div class="flex items-center gap-3">
          <div class="p-2 bg-violet-100 rounded-lg">
            <PhArrowsLeftRight @size={{20}} class="text-violet-600" />
          </div>
          <div>
            <Heading @level={{3}} class="text-lg font-semibold text-gray-900">
              Change Contribution Account
            </Heading>
            <Text class="text-sm text-gray-500">
              Select which account receives employer contributions
            </Text>
          </div>
        </div>
      </:header>
      <:body>
        <div class="space-y-3">
          {{#if @currentAccount}}
            <div class="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
              <PhBank @size={{16}} class="text-gray-400" />
              <Text class="text-sm text-gray-500">
                Currently receiving:
                <span class="font-medium text-gray-900">{{@currentAccount.institutionName}}</span>
              </Text>
            </div>
          {{/if}}

          {{#each @accounts as |account|}}
            <button
              type="button"
              class="w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left
                {{if
                  (eq this.selectedAccountId account.id)
                  'border-violet-500 bg-violet-50 shadow-sm'
                  'border-gray-200 hover:border-violet-300 hover:bg-gray-50'
                }}"
              {{on "click" (fn this.selectAccount account.id)}}
            >
              <div class="flex-shrink-0">
                {{#if (eq this.selectedAccountId account.id)}}
                  <PhCheckCircle @size={{24}} class="text-violet-600" />
                {{else}}
                  <PhRadioButton @size={{24}} class="text-gray-300" />
                {{/if}}
              </div>
              <div class="flex-1 min-w-0">
                <Text class="font-medium text-gray-900 truncate">
                  {{account.institutionName}}
                </Text>
                {{#if account.number}}
                  <Text class="text-sm text-gray-500">
                    Account ····{{lastFour account.number}}
                  </Text>
                {{/if}}
              </div>
              {{#if (and @currentAccount (eq @currentAccount.id account.id))}}
                <span
                  class="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                >
                  Current
                </span>
              {{/if}}
            </button>
          {{else}}
            <div class="text-center py-8">
              <PhBank @size={{48}} class="mx-auto text-gray-300 mb-3" />
              <Text class="text-gray-500">No linked accounts found.</Text>
              <Text class="text-sm text-gray-400">
                Link a loan account to receive employer contributions.
              </Text>
            </div>
          {{/each}}
        </div>
      </:body>
      <:footer>
        <div class="flex justify-end gap-3">
          <Button @appearance="outlined" {{on "click" this.handleClose}}>
            Cancel
          </Button>
          <Button
            @intent="primary"
            @isRunning={{this.updateAccountTask.isRunning}}
            @isDisabled={{this.isSaveDisabled}}
            {{on "click" this.updateAccountTask.perform}}
          >
            Update Account
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>

  // Pure helpers used in template
}

function lastFour(value: string): string {
  return value.slice(-4);
}
